import { useEffect, useRef } from 'react';
import { claimAnalyticsAction, trackEvent } from '../amplitude';

const SCROLL_THRESHOLDS = [25, 50, 75, 100];

const useNewsEngagement = (news, articleRef) => {
  const stateRef = useRef({
    newsId: null,
    elapsed: false,
    readTracked: false,
    maxScroll: 0,
    thresholds: new Set(),
  });

  useEffect(() => {
    if (!news?.id) return undefined;

    const state = {
      newsId: news.id,
      elapsed: false,
      readTracked: false,
      maxScroll: 0,
      thresholds: new Set(),
    };
    stateRef.current = state;
    const startedAt = performance.now();

    const common = {
      news_id: news.id,
      category: news.category || null,
      sections: Array.isArray(news.sections)
        ? news.sections
        : [news.section].filter(Boolean),
    };

    const maybeTrackRead = () => {
      if (!state.elapsed || state.maxScroll < 50 || state.readTracked) return;
      state.readTracked = true;
      trackEvent('news_read', {
        ...common,
        reading_time_seconds: Math.round((performance.now() - startedAt) / 1000),
        max_scroll_percent: state.maxScroll,
      });
    };

    const measureScroll = () => {
      const element = articleRef.current;
      if (!element || stateRef.current !== state) return;
      const rect = element.getBoundingClientRect();
      const viewed = Math.min(rect.height, Math.max(0, window.innerHeight - rect.top));
      const percent = rect.height > 0 ? Math.min(100, Math.floor((viewed / rect.height) * 100)) : 0;
      state.maxScroll = Math.max(state.maxScroll, percent);

      SCROLL_THRESHOLDS.forEach((threshold) => {
        if (state.maxScroll >= threshold && !state.thresholds.has(threshold)) {
          state.thresholds.add(threshold);
          if (claimAnalyticsAction(`news_scroll_depth:${news.id}:${threshold}`)) {
            trackEvent('news_scroll_depth', {
              news_id: news.id,
              scroll_percent: threshold,
              category: news.category || null,
              section: common.sections[0] || null,
            });
          }
        }
      });
      maybeTrackRead();
    };

    const timer = window.setTimeout(() => {
      state.elapsed = true;
      maybeTrackRead();
    }, 15000);
    window.addEventListener('scroll', measureScroll, { passive: true });
    window.addEventListener('resize', measureScroll);
    measureScroll();

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('scroll', measureScroll);
      window.removeEventListener('resize', measureScroll);
    };
  }, [articleRef, news?.category, news?.id, news?.section, news?.sections]);
};

export default useNewsEngagement;
